import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Badge,
  Spinner,
  Card,
  Row,
  Col,
  Form,
  InputGroup,
  Accordion,
  Container,
  ListGroup,
} from "react-bootstrap";
import {
  ArrowLeft,
  Briefcase,
  GeoAlt,
  Rulers,
  Hash,
  FileText,
  Clock,
  Calendar,
  Search,
  Person,
  Envelope,
  Telephone,
  Folder,
  House,
  Building,
  Layers,
  Grid3x3,
  Eye,
  Calculator,
  CheckCircle,
  ListCheck,
  FileEarmarkText,
  PlusCircle,
  ArrowRight,
  PeopleFill,
  
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";

const ProjectsList = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeAccordion, setActiveAccordion] = useState(null);

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH USER AND PROJECTS ================= */
  const fetchUserAndProjects = async () => {
    try {
      setLoading(true);

      // Fetch user details
      const userRes = await axios.get(
        `${API_BASE}/adminUsers/getById/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Fetch user projects
      const projectsRes = await axios.get(
        `${API_BASE}/data/getProjectsByUser/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (userRes.data.success) {
        setUser(userRes.data.data.user);
      } else {
        toast.error("User not found");
        navigate("/users-list");
      }

      if (projectsRes.data.success) {
        setProjects(projectsRes.data.projects || []);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load user projects");
      navigate("/users-list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserAndProjects();
    }
  }, [userId]);

  /* ================= NAVIGATE TO PROJECT DETAILS ================= */
  const handleViewProjectDetails = (projectId) => {
    navigate(`/project-details/${projectId}`);
  };

  /* ================= HELPERS ================= */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateTotals = () => {
    return projects.reduce(
      (totals, project) => ({
        livingSqft: totals.livingSqft + (project.livingSqft || 0),
        slabSqft: totals.slabSqft + (project.slabSqft || 0),
        areaRoofSqft: totals.areaRoofSqft + (project.areaRoofSqft || 0),
        flatworkSqft: totals.flatworkSqft + (project.flatworkSqft || 0),
        count: totals.count + 1,
      }),
      { livingSqft: 0, slabSqft: 0, areaRoofSqft: 0, flatworkSqft: 0, count: 0 }
    );
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.projectLocation
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      project.lotNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInitials = (name) => {
    if (!name) return "UU";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const toggleAccordion = (index) => {
    if (activeAccordion === index) {
      setActiveAccordion(null);
    } else {
      setActiveAccordion(index);
    }
  };

  /* ================= HANDLE FEATURE NAVIGATION ================= */
  const handleFeatureClick = (projectId, feature) => {
    switch (feature) {
      case "bid-estimate-list":
        navigate(`/bid-estimate-list/${projectId}`);
        break;
      case "notes":
        navigate(`/notes/${projectId}`);
        break;
      case "todo":
        navigate(`/todo/${projectId}`);
        break;
      case "forms":
        navigate(`/forms/${projectId}`);
        break;
      case "help-and-support":
        navigate(`/help-and-support/${projectId}`);
        break;
      case "selections":
        navigate(`/selections/${projectId}`);
        break;
      case "cost-variance":
        navigate(`/cost-variance/${projectId}`);
        break;
      case "sub-contractor-bids":
        navigate(`/bid-tasks/subcontractors/${projectId}`);
        break;
      default:
        navigate(`/project-details/${projectId}`);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading projects...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <Container className="py-5 text-center">
        <h4 className="mb-3">User not found</h4>
        <Button variant="primary" onClick={() => navigate("/users-list")}>
          <ArrowLeft className="me-2" />
          Back to Users List
        </Button>
      </Container>
    );
  }

  const totals = calculateTotals();

  return (
    <Container fluid style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate("/users-list")}
                className="mb-3 d-flex align-items-center"
              >
                <ArrowLeft className="me-2" />
                Back to Users List
              </Button>
              <h2 className="fw-bold mb-2">
                <Briefcase className="me-3" />
                {user.name}'s Projects
              </h2>
              <div className="d-flex align-items-center">
                <small className="text-muted">Total Projects:</small>
                <Badge bg="success" className="px-3 py-2 ms-2">
                  <Folder className="me-2" />
                  {projects.length} Project{projects.length !== 1 ? "s" : ""}
                </Badge>
                
              </div>
            </div>

            <div className="text-end">
              <div className="d-flex align-items-center p-2 rounded">
                {user.profilePicture ? (
                  <img
                    src={user.profilePicture}
                    alt={user.name}
                    width={60}
                    height={60}
                    className="rounded-circle me-3"
                  />
                ) : (
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                    style={{ width: 60, height: 60, fontSize: "1.5rem" }}
                  >
                    {getInitials(user.name)}
                  </div>
                )}
                <div className="text-start">
                  <div className="fw-bold fs-5">{user.name}</div>
                  <div className="text-muted small">
                    <Envelope size={12} className="me-1" />
                    {user.email}
                  </div>
                  {user.number && (
                    <div className="text-muted small">
                      <Telephone size={12} className="me-1" />
                      {user.number}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* USER INFO ROW */}
          {/* <Row className="g-3 mb-4">
            <Col md={12}>
              <Card className="h-100 border-start border-4 border-success">
                <Card.Body>
                  <h6 className="text-success mb-3">
                    <Briefcase className="me-2" />
                    Projects Summary
                  </h6>
                  <Row className="g-4">
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                        <div className="fw-bold fs-4 text-success">
                          {totals.count}
                        </div>
                        <small className="text-muted">Total Projects</small>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 bg-primary bg-opacity-10 rounded">
                        <div className="fw-bold fs-4 text-primary">
                          <House className="me-1" />
                          {totals.livingSqft}
                        </div>
                        <small className="text-muted">Living Sqft</small>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 bg-warning bg-opacity-10 rounded">
                        <div className="fw-bold fs-4 text-warning">
                          <Layers className="me-1" />
                          {totals.slabSqft}
                        </div>
                        <small className="text-muted">Slab Sqft</small>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 bg-info bg-opacity-10 rounded">
                        <div className="fw-bold fs-4 text-info">
                          <Building className="me-1" />
                          {totals.areaRoofSqft}
                        </div>
                        <small className="text-muted">Roof Sqft</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row> */}
        </Card.Body>
      </Card>

      {/* PROJECTS LIST */}
      {filteredProjects.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <Briefcase size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Projects Found</h4>
            <p className="text-muted mb-4">
              {searchTerm
                ? "No projects match your search criteria"
                : "This user hasn't created any projects yet"}
            </p>
            {searchTerm && (
              <Button
                variant="outline-primary"
                onClick={() => setSearchTerm("")}
              >
                Clear Search
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="p-1">
              <Accordion activeKey={activeAccordion?.toString()} flush>
                {filteredProjects.map((project, index) => (
                  <Accordion.Item key={project._id} eventKey={index.toString()}>
                    <Accordion.Header onClick={() => toggleAccordion(index)}>
                      <div className="d-flex align-items-center w-100">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center">
                            <Badge
                              bg="primary"
                              className="rounded-circle p-2 me-3"
                            >
                              {index + 1}
                            </Badge>
                            <div>
                              <h5 className="mb-1">{project.name}</h5>
                              <div className="d-flex align-items-center">
                                <small className="text-muted me-3">
                                  <GeoAlt size={12} className="me-1" />
                                  {project.projectLocation}
                                </small>
                                <small className="text-muted">
                                  <Hash size={12} className="me-1" />
                                  Lot: {project.lotNo}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="text-end me-3">
                          <small className="text-muted d-block">Created</small>
                          <small>{formatDate(project.createdAt)}</small>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Row>
                        <Col md={12}>
                          {project.description && (
                            <div className="mb-4">
                              <h6 className="d-flex align-items-center mb-3">
                                <FileText className="me-2 text-primary" />
                                Description
                              </h6>
                              <Card className="bg-light">
                                <Card.Body>
                                  <p className="mb-0">{project.description}</p>
                                </Card.Body>
                              </Card>
                            </div>
                          )}

                          <h6 className="d-flex align-items-center mb-3">
                            <Rulers className="me-2 text-primary" />
                            Project Measurements
                          </h6>
                          <Row className="g-3">
                            <Col xs={6} md={3}>
                              <Card className="text-center h-100">
                                <Card.Body>
                                  <div className="mb-2">
                                    <House size={24} className="text-primary" />
                                  </div>
                                  <small className="text-muted d-block">
                                    Living Area
                                  </small>
                                  <div className="fw-bold h4 text-primary mb-0">
                                    {project.livingSqft}
                                  </div>
                                  <small className="text-muted">sqft</small>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col xs={6} md={3}>
                              <Card className="text-center h-100">
                                <Card.Body>
                                  <div className="mb-2">
                                    <Layers
                                      size={24}
                                      className="text-warning"
                                    />
                                  </div>
                                  <small className="text-muted d-block">
                                    Slab Area
                                  </small>
                                  <div className="fw-bold h4 text-warning mb-0">
                                    {project.slabSqft}
                                  </div>
                                  <small className="text-muted">sqft</small>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col xs={6} md={3}>
                              <Card className="text-center h-100">
                                <Card.Body>
                                  <div className="mb-2">
                                    <Building size={24} className="text-info" />
                                  </div>
                                  <small className="text-muted d-block">
                                    Roof Area
                                  </small>
                                  <div className="fw-bold h4 text-info mb-0">
                                    {project.areaRoofSqft}
                                  </div>
                                  <small className="text-muted">sqft</small>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col xs={6} md={3}>
                              <Card className="text-center h-100">
                                <Card.Body>
                                  <div className="mb-2">
                                    <Grid3x3
                                      size={24}
                                      className="text-success"
                                    />
                                  </div>
                                  <small className="text-muted d-block">
                                    Flatwork Area
                                  </small>
                                  <div className="fw-bold h4 text-success mb-0">
                                    {project.flatworkSqft}
                                  </div>
                                  <small className="text-muted">sqft</small>
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>

                          {/* PROJECT FEATURES */}
                          <div className="mt-4">
                            <h6 className="d-flex align-items-center mb-3">
                              <ListCheck className="me-2 text-primary" />
                              Project Features
                            </h6>
                            <Row className="g-2">
                              <Col md={3}>
                                <Button
                                  variant="outline-primary"
                                  className="w-100 d-flex align-items-center justify-content-center py-3"
                                  onClick={() =>
                                    handleFeatureClick(
                                      project._id,
                                      "bid-estimate-list"
                                    )
                                  }
                                >
                                  <Calculator className="me-2" />
                                  Bid Estimate
                                </Button>
                              </Col>
                              <Col md={3}>
                                <Button
                                  variant="outline-info"
                                  className="w-100 d-flex align-items-center justify-content-center py-3"
                                  onClick={() =>
                                    handleFeatureClick(project._id, "notes")
                                  }
                                >
                                  <FileEarmarkText className="me-2" />
                                  Notes
                                </Button>
                              </Col>
                              <Col md={3}>
                                <Button
                                  variant="outline-warning"
                                  className="w-100 d-flex align-items-center justify-content-center py-3"
                                  onClick={() =>
                                    handleFeatureClick(project._id, "todo")
                                  }
                                >
                                  <CheckCircle className="me-2" />
                                  To-Do List
                                </Button>
                              </Col>
                              <Col md={3}>
                                <Button
                                  variant="outline-success"
                                  className="w-100 d-flex align-items-center justify-content-center py-3"
                                  onClick={() =>
                                    handleFeatureClick(project._id, "forms")
                                  }
                                >
                                  <Folder className="me-2" />
                                  Forms
                                </Button>
                              </Col>
                              <Col md={3}>
                                <Button
                                  variant="outline-secondary"
                                  className="w-100 d-flex align-items-center justify-content-center py-3"
                                  onClick={() =>
                                    handleFeatureClick(
                                      project._id,
                                      "help-and-support"
                                    )
                                  }
                                >
                                  <ListCheck className="me-2" />
                                  Help & Support
                                </Button>
                              </Col>
                              <Col md={3}>
                                <Button
                                  variant="outline-dark"
                                  className="w-100 d-flex align-items-center justify-content-center py-3"
                                  onClick={() =>
                                    handleFeatureClick(
                                      project._id,
                                      "selections"
                                    )
                                  }
                                >
                                  <PlusCircle className="me-2" />
                                  Selections
                                </Button>
                              </Col>
                              <Col md={3}>
                                <Button
                                  variant="outline-success"
                                  className="w-100 d-flex align-items-center justify-content-center py-3"
                                  onClick={() =>
                                    handleFeatureClick(
                                      project._id,
                                      "cost-variance"
                                    )
                                  }
                                >
                                  <Calculator className="me-2" />
                                  Cost Variance
                                </Button>
                              </Col>
                              <Col md={3}>
                                <Button
                                  variant="outline-warning"
                                  className="w-100 d-flex align-items-center justify-content-center py-3"
                                  onClick={() =>
                                    handleFeatureClick(
                                      project._id,
                                      "sub-contractor-bids"
                                    )
                                  }
                                >
                                  <PeopleFill className="me-2" />
                                  Sub-Contractors Bids
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* FOOTER */}
      <div className="mt-4 text-center">
        <Button
          variant="outline-primary"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="me-2"
        >
          Back to Top
        </Button>
        <Button
          variant="outline-secondary"
          onClick={() => navigate("/users-list")}
        >
          <ArrowLeft className="me-2" />
          Return to Users List
        </Button>
      </div>
    </Container>
  );
};

export default ProjectsList;
